import React, { useState, useRef, useCallback } from 'react';
import { useCanvasAnimation } from '../../../hooks/useCanvasAnimation';

interface Proton {
  x: number;
  y: number;
  dx: number; // Normalized direction X
  dy: number; // Normalized direction Y
  radius: number;
  color: string;
}

interface Photon {
  x: number;
  y: number;
  theta: number;
  time: number; // Accumulated life time
}

export function FusionCollider() {
  const [temperature, setTemperature] = useState<number>(30);
  const [pressure, setPressure] = useState<number>(30);
  const [fusionCount, setFusionCount] = useState<number>(0);

  const protonsRef = useRef<Proton[]>([]);
  
  // State machine for fusion
  // "bouncing" -> "fusing" (shrink 0.6s) -> "helium" (render He and spawn photon, split back after 1.5s)
  const fusionStateRef = useRef<"bouncing" | "fusing" | "helium">("bouncing");
  const fusionTimerRef = useRef<number>(0);
  const collisionPointRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const photonRef = useRef<Photon | null>(null);

  // Protons positions before collapse (for interpolation during fusing state)
  const fusingProtonsStartRef = useRef<{ p1: { x: number; y: number }; p2: { x: number; y: number } } | null>(null);

  const initProtons = (width: number, height: number) => {
    const list: Proton[] = [];
    const minDistance = 40;
    
    for (let i = 0; i < 4; i++) {
      let x = 0, y = 0, valid = false;
      while (!valid) {
        x = 40 + Math.random() * (width - 80);
        y = 40 + Math.random() * (height - 80);
        valid = true;
        for (const other of list) {
          const dist = Math.sqrt((other.x - x) ** 2 + (other.y - y) ** 2);
          if (dist < minDistance) valid = false;
        }
      }

      const angle = Math.random() * Math.PI * 2;
      list.push({
        x,
        y,
        dx: Math.cos(angle),
        dy: Math.sin(angle),
        radius: 8,
        color: '#F87171'
      });
    }
    return list;
  };

  const draw = useCallback((ctx: CanvasRenderingContext2D, deltaTime: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    // Initialize protons on first frame
    if (protonsRef.current.length === 0) {
      protonsRef.current = initProtons(width, height);
    }

    // Clear background
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, width, height);

    // Draw Faint Pressure Grid Lines
    if (pressure > 50) {
      const gridOpacity = ((pressure - 50) / 50) * 0.04;
      ctx.strokeStyle = `rgba(255, 255, 255, ${gridOpacity})`;
      ctx.lineWidth = 1;
      for (let x = 0; x < width; x += 30) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }
      for (let y = 0; y < height; y += 30) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }
    }

    const protons = protonsRef.current;
    const baseSpeed = (temperature / 100) * 400 + 50;

    const fusionEnabled = temperature > 70 && pressure > 70;

    if (fusionStateRef.current === "bouncing") {
      // 1. Update Positions
      protons.forEach(p => {
        p.x += p.dx * baseSpeed * deltaTime;
        p.y += p.dy * baseSpeed * deltaTime;

        // Bounce off walls
        if (p.x - p.radius < 0) {
          p.x = p.radius;
          p.dx = -p.dx;
        } else if (p.x + p.radius > width) {
          p.x = width - p.radius;
          p.dx = -p.dx;
        }

        if (p.y - p.radius < 0) {
          p.y = p.radius;
          p.dy = -p.dy;
        } else if (p.y + p.radius > height) {
          p.y = height - p.radius;
          p.dy = -p.dy;
        }
      });

      // 2. Detect collisions between protons
      let collisionDetected = false;
      let p1Idx = -1, p2Idx = -1;

      for (let i = 0; i < protons.length; i++) {
        for (let j = i + 1; j < protons.length; j++) {
          const p1 = protons[i];
          const p2 = protons[j];
          const dx = p2.x - p1.x;
          const dy = p2.y - p1.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < p1.radius + p2.radius) {
            collisionDetected = true;
            p1Idx = i;
            p2Idx = j;

            if (!fusionEnabled) {
              // Elastic collision (swap velocities)
              const tempDx = p1.dx;
              const tempDy = p1.dy;
              p1.dx = p2.dx;
              p1.dy = p2.dy;
              p2.dx = tempDx;
              p2.dy = tempDy;

              // Push apart to prevent overlap/sticking
              const overlap = p1.radius + p2.radius - dist;
              p1.x -= (dx / dist) * overlap * 0.5;
              p1.y -= (dy / dist) * overlap * 0.5;
              p2.x += (dx / dist) * overlap * 0.5;
              p2.y += (dy / dist) * overlap * 0.5;
            }
          }
        }
      }

      // 3. Handle Fusion trigger
      if (collisionDetected && fusionEnabled && p1Idx !== -1 && p2Idx !== -1) {
        const p1 = protons[p1Idx];
        const p2 = protons[p2Idx];

        // Store positions for animation
        fusingProtonsStartRef.current = {
          p1: { x: p1.x, y: p1.y },
          p2: { x: p2.x, y: p2.y }
        };

        collisionPointRef.current = {
          x: (p1.x + p2.x) / 2,
          y: (p1.y + p2.y) / 2
        };

        fusionStateRef.current = "fusing";
        fusionTimerRef.current = 0;
      }

      // 4. Render Bouncing Protons
      protons.forEach(p => {
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fill();

        // Plus symbol label
        ctx.fillStyle = '#FFFFFF';
        ctx.font = 'bold 8px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('+1', p.x, p.y);
      });

    } else if (fusionStateRef.current === "fusing") {
      // Bouncing protons are locked/frozen, only the 2 merging shrink
      fusionTimerRef.current += deltaTime;

      const t = Math.min(fusionTimerRef.current / 0.6, 1);
      const cx = collisionPointRef.current.x;
      const cy = collisionPointRef.current.y;
      const pStarts = fusingProtonsStartRef.current;

      if (pStarts) {
        // Draw Proton 1 shrinking towards midpoint
        const px1 = pStarts.p1.x + (cx - pStarts.p1.x) * t;
        const py1 = pStarts.p1.y + (cy - pStarts.p1.y) * t;
        ctx.fillStyle = '#F87171';
        ctx.beginPath();
        ctx.arc(px1, py1, 8 * (1 - t), 0, Math.PI * 2);
        ctx.fill();

        // Draw Proton 2 shrinking towards midpoint
        const px2 = pStarts.p2.x + (cx - pStarts.p2.x) * t;
        const py2 = pStarts.p2.y + (cy - pStarts.p2.y) * t;
        ctx.fillStyle = '#F87171';
        ctx.beginPath();
        ctx.arc(px2, py2, 8 * (1 - t), 0, Math.PI * 2);
        ctx.fill();
      }

      if (fusionTimerRef.current >= 0.6) {
        // Transition to Helium state
        fusionStateRef.current = "helium";
        fusionTimerRef.current = 0;

        // Spawn a photon in random direction
        photonRef.current = {
          x: cx,
          y: cy,
          theta: Math.random() * Math.PI * 2,
          time: 0
        };

        // Increment count
        setFusionCount(prev => prev + 1);
      }

    } else if (fusionStateRef.current === "helium") {
      fusionTimerRef.current += deltaTime;
      const cx = collisionPointRef.current.x;
      const cy = collisionPointRef.current.y;

      // Draw Helium Nucleus with soft radial green glow
      const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, 32);
      glow.addColorStop(0, 'rgba(52, 211, 153, 0.35)');
      glow.addColorStop(1, 'rgba(52, 211, 153, 0)');
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(cx, cy, 32, 0, Math.PI * 2);
      ctx.fill();

      // Main core
      ctx.fillStyle = '#34D399'; // Bright green
      ctx.beginPath();
      ctx.arc(cx, cy, 14, 0, Math.PI * 2);
      ctx.fill();

      // Label
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 10px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('He', cx, cy);

      // Render & Update Photon (with motion blur)
      if (photonRef.current) {
        const photon = photonRef.current;
        photon.time += deltaTime;

        const photonSpeed = 500; // px/sec

        // Draw 3 dots along recent path for blur
        for (let i = 0; i < 3; i++) {
          const lagTime = photon.time - (i * 0.025);
          if (lagTime < 0) continue;

          const px = cx + Math.cos(photon.theta) * photonSpeed * lagTime;
          const py = cy + Math.sin(photon.theta) * photonSpeed * lagTime;
          const opacity = Math.max(0, (1 - (lagTime / 0.5)) * (1 - i * 0.3));

          ctx.fillStyle = `rgba(252, 220, 77, ${opacity})`;
          ctx.beginPath();
          ctx.arc(px, py, 4, 0, Math.PI * 2);
          ctx.fill();
        }

        if (photon.time >= 0.5) {
          photonRef.current = null;
        }
      }

      if (fusionTimerRef.current >= 1.5) {
        // Reset cycle back to scattered protons
        protonsRef.current = initProtons(width, height);
        fusionStateRef.current = "bouncing";
        fusingProtonsStartRef.current = null;
      }
    }

    // Top-Left Simulation Label
    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.font = '14px sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText('Nuclear Fusion & Stellar Energy Simulation', 12, 24);

    // Top-Right HUD Fusion Score
    ctx.fillStyle = '#22D3EE'; // Cyan
    ctx.font = '14px sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(`Fusions: ${fusionCount}`, width - 100, 24);
  }, [temperature, pressure, fusionCount]);

  const canvasRef = useCanvasAnimation(draw, { height: 340 });

  const isFusionConditionsMet = temperature > 70 && pressure > 70;

  return (
    <div className="relative w-full h-[340px] rounded-xl overflow-hidden bg-[#0F172A] border border-white/10">
      <canvas ref={canvasRef} className="block w-full h-full" />

      {/* Warning/Success Banner overlay */}
      <div className="absolute top-3 left-1/2 transform -translate-x-1/2 bg-[#0b0f19]/80 backdrop-blur-sm px-4 py-1.5 rounded-full border border-white/10 text-xs font-semibold select-none pointer-events-none">
        {isFusionConditionsMet ? (
          <span className="text-emerald-400">🔥 Fusion conditions met!</span>
        ) : (
          <span className="text-rose-400">⚡ Too cold/weak to fuse</span>
        )}
      </div>

      {/* Vertical Sliders Overlay on the right */}
      <div className="absolute top-12 right-3 bottom-3 bg-[#0b0f19]/80 backdrop-blur-sm px-3 py-4 rounded-lg border border-white/10 flex gap-4 select-none">
        <div className="flex flex-col items-center gap-1.5 h-full">
          <span className="text-[9px] font-bold text-rose-300 uppercase tracking-wide">Temp</span>
          <input
            type="range"
            min="0"
            max="100"
            value={temperature}
            onChange={(e) => setTemperature(parseFloat(e.target.value))}
            className="w-1 cursor-pointer accent-rose-500 h-full bg-white/20 rounded-lg appearance-none"
            style={{ writingMode: 'vertical-lr', direction: 'rtl' }}
          />
          <span className="text-[10px] text-white font-bold">{Math.round(temperature)}%</span>
        </div>

        <div className="flex flex-col items-center gap-1.5 h-full">
          <span className="text-[9px] font-bold text-violet-300 uppercase tracking-wide">Pres</span>
          <input
            type="range"
            min="0"
            max="100"
            value={pressure}
            onChange={(e) => setPressure(parseFloat(e.target.value))}
            className="w-1 cursor-pointer accent-violet-500 h-full bg-white/20 rounded-lg appearance-none"
            style={{ writingMode: 'vertical-lr', direction: 'rtl' }}
          />
          <span className="text-[10px] text-white font-bold">{Math.round(pressure)}%</span>
        </div>
      </div>
    </div>
  );
}
